/**
 * UnsplashImagePicker Component
 *
 * Reusable Unsplash image search component with horizontal scroll and load more.
 * Used in AI Magic Panel and AI Briefing Wizard.
 */

import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './Icon';
import { RetryImage } from './RetryImage';
import { ICONS } from '@/constants/icons';
import api from '@/services/api';

// ============================================================================
// Types
// ============================================================================

export interface UnsplashImage {
  id: string;
  url: string;
  thumbnailUrl: string;
  downloadUrl: string;
  photographer: string;
  photographerUrl: string;
}

interface UnsplashImagePickerProps {
  /**
   * Callback when an image is selected
   */
  onSelectImage: (image: UnsplashImage) => void;

  /**
   * Optional initial search query
   */
  initialQuery?: string;

  /**
   * Auto-search on mount if initialQuery is provided
   */
  autoSearch?: boolean;

  /**
   * Number of images per page (default: 6)
   */
  perPage?: number;

  /**
   * Size variant
   */
  size?: 'sm' | 'md';

  /**
   * Disabled state
   */
  disabled?: boolean;

  /**
   * Optional class name
   */
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

export const UnsplashImagePicker = ({
  onSelectImage,
  initialQuery = '',
  autoSearch = false,
  perPage = 6,
  size = 'sm',
  disabled = false,
  className = '',
}: UnsplashImagePickerProps) => {
  const { t } = useTranslation('common');

  // State
  const [query, setQuery] = useState(initialQuery);
  const [hasAutoSearched, setHasAutoSearched] = useState(false);
  const [images, setImages] = useState<UnsplashImage[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Size variants
  const imageSize = size === 'sm' ? 'w-20 h-20' : 'w-24 h-24';

  /**
   * Parse Unsplash API response
   */
  const parseImages = (data: {
    id: string;
    src: string;
    thumb: string;
    downloadUrl: string;
    photographer: { name: string; profileUrl: string };
  }[]): UnsplashImage[] => {
    return data.map((img) => ({
      id: img.id,
      url: img.src,
      thumbnailUrl: img.thumb,
      downloadUrl: img.downloadUrl,
      photographer: img.photographer.name,
      photographerUrl: img.photographer.profileUrl,
    }));
  };

  /**
   * Search images (initial search)
   */
  const handleSearch = useCallback(async () => {
    if (!query.trim() || disabled) return;

    setIsSearching(true);
    setPage(1);
    setHasSearched(true);
    try {
      const response = await api.get('/api/unsplash/search', {
        params: { query: query.trim(), per_page: perPage, page: 1 },
      });

      const newImages = parseImages(response.data.data.images);
      setImages(newImages);
      setHasMore(newImages.length >= perPage);
    } catch (error) {
      console.error('Unsplash search failed:', error);
      setImages([]);
      setHasMore(false);
    } finally {
      setIsSearching(false);
    }
  }, [query, perPage, disabled]);

  /**
   * Load more images
   */
  const handleLoadMore = useCallback(async () => {
    if (!query.trim() || isLoadingMore || disabled) return;

    setIsLoadingMore(true);
    const nextPage = page + 1;
    try {
      const response = await api.get('/api/unsplash/search', {
        params: { query: query.trim(), per_page: perPage, page: nextPage },
      });

      const newImages = parseImages(response.data.data.images);
      setImages((prev) => [...prev, ...newImages]);
      setPage(nextPage);
      setHasMore(newImages.length >= perPage);
    } catch (error) {
      console.error('Unsplash load more failed:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [query, page, perPage, isLoadingMore, disabled]);

  /**
   * Handle key press in search input
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  /**
   * Auto-search on mount if enabled and initialQuery is provided
   */
  useEffect(() => {
    if (autoSearch && initialQuery.trim() && !hasAutoSearched) {
      setHasAutoSearched(true);
      handleSearch();
    }
  }, [autoSearch, initialQuery, hasAutoSearched, handleSearch]);

  return (
    <div className={`min-w-0 ${className}`}>
      {/* Search Input */}
      <div className="flex gap-2 mb-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('unsplash_search_placeholder', { defaultValue: 'Search images...' })}
          disabled={disabled || isSearching}
          className="flex-1 px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-50"
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          onClick={handleSearch}
          disabled={disabled || isSearching || !query.trim()}
          className="px-2.5 py-1.5 bg-primary-500 hover:bg-primary-600 text-primary-contrast text-sm font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSearching ? (
            <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
          ) : (
            <Icon name={ICONS.SEARCH} size="sm" />
          )}
        </button>
      </div>

      {/* Loading State */}
      {isSearching && (
        <div className="text-center py-4">
          <div className="animate-spin w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full mx-auto mb-2" />
          <p className="text-xs text-gray-500">{t('unsplash_searching', { defaultValue: 'Searching...' })}</p>
        </div>
      )}

      {/* Image Results - Horizontal scroll */}
      {images.length > 0 && !isSearching && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {images.map((image) => (
            <button
              key={image.id}
              type="button"
              aria-label={`${t('select_image')}: ${image.photographer}`}
              className={`group relative flex-shrink-0 ${imageSize} bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer`}
              onClick={() => onSelectImage(image)}
            >
              <RetryImage
                src={image.thumbnailUrl}
                alt={`Photo by ${image.photographer}`}
                className="w-full h-full object-cover"
              />
              {/* Overlay on hover */}
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:bg-transparent [@media(hover:none)]:opacity-100">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-black/60">
                  <Icon name={ICONS.PLUS} size="sm" className="text-white" ariaHidden />
                </span>
              </span>
            </button>
          ))}
          {/* Load More Button */}
          {hasMore && (
            <button
              type="button"
              onClick={handleLoadMore}
              disabled={isLoadingMore || disabled}
              className={`flex-shrink-0 ${imageSize} flex flex-col items-center justify-center gap-1 border border-dashed border-gray-300 rounded-lg hover:border-primary-400 hover:bg-primary-50 transition-all disabled:opacity-50`}
            >
              {isLoadingMore ? (
                <span className="animate-spin w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full" />
              ) : (
                <>
                  <Icon name={ICONS.PLUS} size="sm" className="text-gray-400" />
                  <span className="text-xs text-gray-500">{t('unsplash_more', { defaultValue: 'More' })}</span>
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* No Results */}
      {images.length === 0 && !isSearching && hasSearched && (
        <p className="text-xs text-gray-500 text-center py-3">
          {t('unsplash_no_results', { defaultValue: 'No images found' })}
        </p>
      )}

      {/* Unsplash Attribution */}
      <p className="text-xs text-gray-400 text-center mt-1 leading-none pb-0">
        {t('unsplash_powered_by', { defaultValue: 'Powered by Unsplash' })}
      </p>
    </div>
  );
};

export default UnsplashImagePicker;
